// Pure logic behind the workspace "My lanes" board (WorkspaceRates.tsx): which price a box reads, how a
// lane's sailings collapse into one offer per liner, and how the port pickers resolve typed text.
// No I/O and no React, so bun can pin it. Only cleanPort is imported as a value from ./rates.
import type { LaneReliability, LaneRow, Sailing } from './rates'
import { cleanPort } from './rates'

export type Box = '20gp' | '40gp' | '40hc'
export const BOXES: ReadonlyArray<{ id: Box; label: string }> = [
  { id: '20gp', label: "20'" },
  { id: '40gp', label: "40'" },
  { id: '40hc', label: "40' HC" },
]

export const toBox = (v: string | null | undefined): Box =>
  v === '20gp' || v === '40gp' || v === '40hc' ? v : '40hc'

/** The price a sailing quotes for the chosen box; null when the liner shows none (never 0). */
export function boxPrice(s: Pick<LaneRow, 'price_20gp' | 'price_40gp' | 'price_40hc'>, box: Box) {
  const p = box === '20gp' ? s.price_20gp : box === '40gp' ? s.price_40gp : s.price_40hc
  return p != null && p > 0 ? p : null
}

export const BOX_IDS: ReadonlyArray<Box> = ['20gp', '40gp', '40hc']

export interface CarrierOffer {
  carrier: string
  /**
   * The liner's cheapest sailing for the box the board ranks by (earliest ETD on a tie). If the liner shows
   * no price for that box, its soonest priced sailing, so the row can still say when and on what it sails.
   */
  cheapest: Sailing
  /** The liner's price for the ranking box; null when it quotes none for it (it may for another box). */
  price: number | null
  /** The liner's own lowest price per box, across all its sailings on the lane. */
  prices: Record<Box, number | null>
  /** Sailings the liner has on the lane with any price. */
  sailings: number
  /** Soonest ETD among those sailings. */
  nextEtd: string | null
}

const byEtd = (a: string | null, b: string | null) =>
  a == null ? (b == null ? 0 : 1) : b == null ? -1 : a < b ? -1 : a > b ? 1 : 0

const lower = (a: number | null, b: number | null) =>
  a == null ? b : b == null ? a : Math.min(a, b)

/**
 * One row per liner. Every box a liner quotes is kept, so the board can show 20', 40' and 40' HC side by side;
 * `box` only decides the ranking. Liners with a price for the ranking box come first, cheapest first; the rest
 * follow by name. A sailing with no price at all is left out (a blank is not a free rate) and counted in
 * `unpriced`. `hidden` removes liners the customer does not want; `liners` lists every liner seen, hidden or
 * not, so the customer can bring one back.
 */
export function offersByCarrier(
  sailings: Sailing[],
  box: Box,
  hidden: ReadonlySet<string> = new Set()
): { offers: CarrierOffer[]; unpriced: number; liners: string[] } {
  const by = new Map<string, CarrierOffer>()
  let unpriced = 0
  for (const s of sailings) {
    const prices: Record<Box, number | null> = {
      '20gp': boxPrice(s, '20gp'),
      '40gp': boxPrice(s, '40gp'),
      '40hc': boxPrice(s, '40hc'),
    }
    if (prices['20gp'] == null && prices['40gp'] == null && prices['40hc'] == null) {
      unpriced++
      continue
    }
    const carrier = (s.carrier ?? '').trim() || 'Unknown'
    const cur = by.get(carrier)
    if (!cur) {
      by.set(carrier, {
        carrier,
        cheapest: s,
        price: prices[box],
        prices: { ...prices },
        sailings: 1,
        nextEtd: s.etd,
      })
      continue
    }
    cur.sailings++
    if (byEtd(s.etd, cur.nextEtd) < 0) cur.nextEtd = s.etd
    const held = boxPrice(cur.cheapest, box)
    const p = prices[box]
    const better =
      p != null
        ? held == null || p < held || (p === held && byEtd(s.etd, cur.cheapest.etd) < 0)
        : held == null && byEtd(s.etd, cur.cheapest.etd) < 0
    if (better) cur.cheapest = s
    for (const b of BOX_IDS) cur.prices[b] = lower(cur.prices[b], prices[b])
    cur.price = cur.prices[box]
  }
  const all = [...by.values()]
  const offers = all
    .filter((o) => !hidden.has(o.carrier))
    .sort((a, b) =>
      a.price != null && b.price != null
        ? a.price - b.price || a.carrier.localeCompare(b.carrier)
        : a.price != null
          ? -1
          : b.price != null
            ? 1
            : a.carrier.localeCompare(b.carrier)
    )
  return { offers, unpriced, liners: all.map((o) => o.carrier).sort((a, b) => a.localeCompare(b)) }
}

export interface LaneSummary {
  best: number | null
  bestCarrier: string | null
  /** Dearest priced liner's price minus the cheapest, so "how much does picking the wrong liner cost". */
  spread: number | null
  carriers: number
  /** Newest source refresh across the lane's sailings. */
  updatedAt: string | null
}

export function summarize(offers: CarrierOffer[], sailings: Sailing[]): LaneSummary {
  const priced = offers.filter((o) => o.price != null)
  const first = priced[0]
  const last = priced[priced.length - 1]
  let updatedAt: string | null = null
  for (const s of sailings) {
    if (s.source_updated_at && (!updatedAt || s.source_updated_at > updatedAt))
      updatedAt = s.source_updated_at
  }
  return {
    best: first?.price ?? null,
    bestCarrier: first?.carrier ?? null,
    spread:
      first?.price != null && last?.price != null && priced.length > 1
        ? last.price - first.price
        : null,
    carriers: offers.length,
    updatedAt,
  }
}

/** Change from the first to the last daily median in the series, in percent; null if it can't be told. */
export function trendPct(series: number[]): number | null {
  if (series.length < 2) return null
  const a = series[0]
  const b = series[series.length - 1]
  if (a == null || b == null || a <= 0) return null
  return ((b - a) / a) * 100
}

/**
 * A port's display name. The feed carries "Manzanillo,Mexico", "Busan, Korea", "Shanghai, Shanghai": the
 * first comma-separated part is the port. A port with no usable name shows its code (cleanPort would
 * title-case the code into "Cnaqg").
 */
export function portName(raw: string | null | undefined, code: string): string {
  const head =
    (raw ?? '')
      .split(',')[0]
      ?.replace(/\(.*?\)/g, '')
      .trim() ?? ''
  const cleaned = head ? cleanPort(head, code) : ''
  return !cleaned || cleaned.toUpperCase() === code.toUpperCase() ? code : cleaned
}

// Ports the customer treats as one place (Shenzhen is Yantian): one table shared with the API, in
// apps/api/src/lib/port-alias.ts. The picker offers the real port under the name people search for.
import { PORT_ALIASES, canonicalPort } from '../../../api/src/lib/port-alias'
export { PORT_ALIASES, canonicalPort }
const aliasNames = new Map(Object.values(PORT_ALIASES).map((a) => [a.to, a.name]))

export interface PortOption {
  code: string
  name: string
}

/**
 * Port choices for a picker: every port the live lane feed knows, named ones first and busiest first,
 * topped up from a seed list so the picker is never empty when the feed is slow.
 */
export function buildPortOptions(
  lanes: LaneRow[],
  role: 'pol' | 'pod',
  seed: ReadonlyArray<{ code: string; name: string }> = []
): PortOption[] {
  const m = new Map<string, PortOption & { n: number }>()
  for (const l of lanes) {
    const code = role === 'pol' ? l.pol_code : l.pod_code
    if (!code) continue
    const raw = role === 'pol' ? l.pol_name : l.pod_name
    const name = portName(raw, code)
    const e = m.get(code)
    if (e) {
      e.n++
      if (e.name === code && name && name !== code) e.name = name
    } else m.set(code, { code, name: name || code, n: 1 })
  }
  for (const p of seed) if (!m.has(p.code)) m.set(p.code, { code: p.code, name: p.name, n: 0 })
  // An alias source (Shenzhen CNSZX) is not offered; its target is, under the name the customer types.
  for (const from of Object.keys(PORT_ALIASES)) m.delete(from)
  for (const [to, name] of aliasNames) {
    const e = m.get(to)
    if (e) e.name = name
  }
  return [...m.values()]
    .sort(
      (a, b) =>
        Number(b.name !== b.code) - Number(a.name !== a.code) ||
        b.n - a.n ||
        a.name.localeCompare(b.name)
    )
    .map(({ code, name }) => ({ code, name }))
}

/** "Shanghai (CNSHA)" is the label the picker offers and the text it hands back. */
export const portLabel = (p: PortOption) => (p.name === p.code ? p.code : `${p.name} (${p.code})`)

/** Turn what was typed into a port code: the label, a bare code, or an exact name (busiest first). Null if it matches none. */
export function resolvePort(input: string, options: PortOption[]): string | null {
  const t = input.trim().toLowerCase()
  if (!t) return null
  // A bare alias source (the code CNSZX, or plain "Shenzhen") lands on its target.
  const viaAlias = Object.entries(PORT_ALIASES).find(
    ([from, a]) => t === from.toLowerCase() || t === a.name.toLowerCase() || t === 'shenzhen'
  )
  if (viaAlias && options.some((p) => p.code === viaAlias[1].to)) return viaAlias[1].to
  for (const p of options) if (portLabel(p).toLowerCase() === t) return p.code
  for (const p of options) if (p.code.toLowerCase() === t) return p.code
  // Two ports can share a name; the options are busiest first, so the first match is the one meant.
  return options.find((p) => p.name.toLowerCase() === t)?.code ?? null
}

/** "2026-10-14T00:00:00Z" -> "14 Oct" (UTC, so the day never shifts with the viewer's zone). */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

export function agoLabel(iso: string | null | undefined, now = Date.now()): string {
  // The customer sees that the rate is current, not when it was read: the exact hour adds worry, not
  // information. Bookable cards are re-seen inside 14 days by construction, so an older stamp (or none) means
  // we cannot say "recently" truthfully, and then we say nothing.
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t) || now - t > 14 * 86_400_000) return ''
  return 'Updated recently'
}

const squash = (v: string) =>
  v
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * The ports a sailing is transhipped through. The harvest lists the whole routing, origin and destination
 * included, under varying spellings ("SHANGHAI", "SHANGHAI, CHINA", "MANZANILLO (MEXICO)"), so any entry that
 * names the lane's own origin or destination is dropped, and repeats are collapsed.
 */
export function transshipPorts(
  legs: ReadonlyArray<{ portName?: string | null }> | null | undefined,
  ...laneNames: Array<string | null | undefined>
): string[] {
  const own = laneNames.map((n) => squash(n ?? '')).filter(Boolean)
  const out: string[] = []
  const seen = new Set<string>()
  for (const leg of legs ?? []) {
    const name = leg.portName?.trim()
    if (!name) continue
    const key = squash(name)
    if (!key || seen.has(key) || own.some((o) => key.includes(o) || o.includes(key))) continue
    seen.add(key)
    out.push(cleanPort(name))
  }
  return out
}

// ── Sorting the board ───────────────────────────────────────────────────────────────────────────

export type SortMode = 'added' | 'price' | 'move' | 'name'
export const SORTS: ReadonlyArray<{ id: SortMode; label: string }> = [
  { id: 'added', label: 'Newest first' },
  { id: 'price', label: 'Cheapest first' },
  { id: 'move', label: 'Biggest move' },
  { id: 'name', label: 'Name' },
]
export const toSort = (v: string | null | undefined): SortMode =>
  v === 'price' || v === 'move' || v === 'name' ? v : 'added'

export interface LaneMetrics {
  /** Cheapest liner's price for the chosen box; null while loading or when nothing is priced. */
  best: number | null
  /** Change over the history window, in percent; null when it cannot be told. */
  change: number | null
}

/**
 * Pinned lanes first, in the order they were added; then the rest by the chosen mode. A lane without the
 * figure the mode sorts on goes last, so a lane still loading never jumps to the top. Stable throughout.
 */
export function sortLanes<T extends { id: string; name: string }>(
  lanes: ReadonlyArray<T>,
  pinned: ReadonlySet<string>,
  mode: SortMode,
  metrics: ReadonlyMap<string, LaneMetrics>
): T[] {
  const idx = new Map(lanes.map((l, i) => [l.id, i]))
  const key = (l: T): number | null => {
    const m = metrics.get(l.id)
    if (mode === 'price') return m?.best ?? null
    if (mode === 'move') return m?.change == null ? null : -Math.abs(m.change)
    return null
  }
  return [...lanes].sort((a, b) => {
    const pa = pinned.has(a.id) ? 0 : 1
    const pb = pinned.has(b.id) ? 0 : 1
    if (pa !== pb) return pa - pb
    if (mode === 'name') {
      const c = a.name.localeCompare(b.name)
      if (c) return c
    } else if (mode !== 'added') {
      const ka = key(a)
      const kb = key(b)
      if (ka == null && kb != null) return 1
      if (ka != null && kb == null) return -1
      if (ka != null && kb != null && ka !== kb) return ka - kb
    }
    return (idx.get(a.id) ?? 0) - (idx.get(b.id) ?? 0)
  })
}

// ── The customer's own index ────────────────────────────────────────────────────────────────────

export interface DayValue {
  day: string
  value: number
}
export interface IndexPoint {
  day: string
  index: number
}

/** A day's median rests on this many priced cards at least, or it is not used (one card is an outlier, not a market). */
export const MIN_DAY_SAMPLE = 3

/**
 * An index of the customer's lanes, 100 on the first day anything is known.
 *
 * Each lane is carried forward over days it has no reading, so every change it makes is counted exactly once
 * and its total change over the window is its last reading against its first. Each day then moves by the MEAN
 * OF THE LOG CHANGES of the lanes that exist on both sides of it, compounded: the index is the geometric mean
 * of the lanes' own movements. Two things the obvious versions get wrong, both found on live data:
 *   - the arithmetic mean of daily changes inflates a noisy series (two lanes that did not move read 102.8
 *     after six days, and the live board read +41% over lanes that moved +10% and -6%);
 *   - comparing only days where a lane has a reading on both sides drops the change back from an outlier
 *     whenever a day in between is missing, and the line drifts.
 * A lane that joins later contributes only its own changes from then on, so adding a lane cannot make the
 * index jump. Callers pass points that already meet MIN_DAY_SAMPLE.
 */
export function basketIndex(histories: ReadonlyArray<ReadonlyArray<DayValue>>): IndexPoint[] {
  const byLane = histories.map(
    (h) => new Map(h.filter((p) => p.day && p.value > 0).map((p) => [p.day, p.value]))
  )
  const days = [...new Set(byLane.flatMap((m) => [...m.keys()]))].sort()
  const first = days[0]
  if (days.length < 2 || first === undefined) return []
  // Forward-fill: from its first reading on, a lane holds its latest reading until it has a new one.
  const filled = byLane.map((m) => {
    const out = new Map<string, number>()
    let last: number | undefined
    for (const d of days) {
      last = m.get(d) ?? last
      if (last !== undefined) out.set(d, last)
    }
    return out
  })
  const out: IndexPoint[] = [{ day: first, index: 100 }]
  let level = 100
  for (let k = 1; k < days.length; k++) {
    const prev = days[k - 1] as string
    const day = days[k] as string
    const moves: number[] = []
    for (const m of filled) {
      const a = m.get(prev)
      const b = m.get(day)
      if (a && b) moves.push(Math.log(b / a))
    }
    if (moves.length) level *= Math.exp(moves.reduce((s, v) => s + v, 0) / moves.length)
    out.push({ day, index: level })
  }
  return out
}

/** Change of the index over its window, in percent. */
export function indexChange(points: ReadonlyArray<IndexPoint>): number | null {
  const last = points[points.length - 1]
  return last && points.length > 1 ? last.index - 100 : null
}

// ── On-time per liner ───────────────────────────────────────────────────────────────────────────

export interface OnTime {
  pct: number
  observations: number
}
/** Liner code (upper case) to its observed on-time figure on one lane. */
export function onTimeByCarrier(rows: ReadonlyArray<LaneReliability>): Map<string, OnTime> {
  const m = new Map<string, OnTime>()
  for (const r of rows) {
    if (!r.carrier || r.on_time_pct == null) continue
    const key = r.carrier.trim().toUpperCase()
    const cur = m.get(key)
    if (!cur || (r.observations ?? 0) > cur.observations)
      m.set(key, { pct: r.on_time_pct, observations: r.observations ?? 0 })
  }
  return m
}
/** Below this many observed sailings an on-time figure is a hint, not a result, and is shown muted. */
export const THIN_SAMPLE = 10

// ── CSV ─────────────────────────────────────────────────────────────────────────────────────────

const csvCell = (v: string | number | null | undefined): string => {
  const s = v == null ? '' : String(v)
  // A leading = + - @ makes a spreadsheet read the cell as a formula; a single quote defuses it.
  const safe = /^[=+\-@]/.test(s) && Number.isNaN(Number(s)) ? `'${s}` : s
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}
export function toCsv(
  rows: ReadonlyArray<ReadonlyArray<string | number | null | undefined>>
): string {
  return `${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`
}

export const CSV_HEADER = [
  'Origin',
  'Destination',
  'Liner',
  "20' USD",
  "40' USD",
  "40' HC USD",
  'Ranked by',
  'Gap to best USD',
  'Transit days',
  'Next sailing',
  'Vessel',
  'Via',
  'Sailings',
  'On-time %',
  'On-time sample',
]

// ── What the customer chooses to see ────────────────────────────────────────────────────────────

export type ColumnId =
  | 'p20gp'
  | 'p40gp'
  | 'p40hc'
  | 'gap'
  | 'transit'
  | 'next'
  | 'vessel'
  | 'sailings'
  | 'ontime'
export const COLUMNS: ReadonlyArray<{ id: ColumnId; label: string }> = [
  { id: 'p20gp', label: "20' price" },
  { id: 'p40gp', label: "40' price" },
  { id: 'p40hc', label: "40' HC price" },
  { id: 'gap', label: 'Gap to best' },
  { id: 'transit', label: 'Transit' },
  { id: 'next', label: 'Next sailing' },
  { id: 'vessel', label: 'Vessel and via' },
  { id: 'sailings', label: 'Sailings' },
  { id: 'ontime', label: 'On-time' },
]
export const DEFAULT_COLUMNS: ReadonlyArray<ColumnId> = COLUMNS.map((c) => c.id)
const COLUMN_IDS = new Set<string>(DEFAULT_COLUMNS)

/** A remembered column choice. Anything that is not a list of known columns falls back to all of them. */
export function toColumns(v: unknown): ColumnId[] {
  if (!Array.isArray(v)) return [...DEFAULT_COLUMNS]
  const seen = v.filter((x): x is ColumnId => typeof x === 'string' && COLUMN_IDS.has(x))
  // Keep the canonical order whatever order they were saved in.
  return DEFAULT_COLUMNS.filter((c) => seen.includes(c))
}

export const boxColumn = (box: Box): ColumnId =>
  box === '20gp' ? 'p20gp' : box === '40gp' ? 'p40gp' : 'p40hc'

/** A remembered list of liners to hide: strings only, upper-cased, de-duplicated. */
export function toHidden(v: unknown): Set<string> {
  if (!Array.isArray(v)) return new Set()
  return new Set(
    v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim())
  )
}

/** The CSV rows for one lane: one per liner, all three box prices, whatever columns are on screen. */
export function laneCsvRows(
  lane: { pol: string; pod: string; polName: string; podName: string },
  offers: ReadonlyArray<CarrierOffer>,
  onTime: ReadonlyMap<string, OnTime>,
  box: Box,
  best: number | null
): Array<Array<string | number | null>> {
  const rank = BOXES.find((b) => b.id === box)?.label ?? box
  return offers.map((o) => {
    const ot = onTime.get(o.carrier.toUpperCase())
    const via = transshipPorts(
      o.cheapest.raw?.transferList,
      lane.polName,
      lane.podName,
      lane.pol,
      lane.pod
    )
    return [
      `${lane.polName} (${lane.pol})`,
      `${lane.podName} (${lane.pod})`,
      o.carrier,
      o.prices['20gp'],
      o.prices['40gp'],
      o.prices['40hc'],
      rank,
      o.price != null && best != null ? o.price - best : null,
      o.cheapest.transit_days ?? null,
      o.nextEtd ? o.nextEtd.slice(0, 10) : null,
      [o.cheapest.vessel, o.cheapest.voyage].filter(Boolean).join(' ') || null,
      via.join(', ') || null,
      o.sailings,
      ot ? Math.round(ot.pct) : null,
      ot ? ot.observations : null,
    ]
  })
}

/** A port as the customer reads it: an aliased port under the name they search for, otherwise a clean name. */
export function displayPort(code: string, stored?: string | null): string {
  return aliasNames.get(code) ?? portName(stored, code)
}

/** Below this, 40' is priced oddly close to 20'. Market quotes put the 40' at roughly 1.5 to 2 times the 20'. */
export const CLOSE_BOX_RATIO = 1.35

/**
 * How the 40' is priced against the 20' on this lane: the median, over the liners that quote both, of the
 * 40' HC (else the 40') over the 20'. Null with fewer than three such liners, because one or two cards are
 * an anecdote. The board says so when it is low: the figures are the source's own listing prices, and a 20'
 * listed barely under the 40' (the source even lists some 20' base rates above the 40') is not something to
 * book on without asking the liner.
 */
export function boxRatio(
  offers: ReadonlyArray<CarrierOffer>
): { ratio: number; liners: number } | null {
  const rs = offers
    .map((o) => {
      const big = o.prices['40hc'] ?? o.prices['40gp']
      const small = o.prices['20gp']
      return big != null && small != null && small > 0 ? big / small : null
    })
    .filter((r): r is number => r != null)
    .sort((a, b) => a - b)
  if (rs.length < 3) return null
  const mid = Math.floor(rs.length / 2)
  const ratio =
    rs.length % 2 ? (rs[mid] as number) : ((rs[mid - 1] as number) + (rs[mid] as number)) / 2
  return { ratio, liners: rs.length }
}
